import React, { useState, useRef, useContext, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Card, Nav, Badge } from 'react-bootstrap';
import '../../../assests/css/admin/AdmincmsManagement/Principalcms.css';

import axios from 'axios';
import { AppContext } from '../../../context/Appcontext';

// Preset icon keys shown on the public Principal page for each achievement card.
// Keeping this list small keeps the admin form simple — the admin only picks a
// meaning, the actual SVG markup lives on the public page component.
const ACHIEVEMENT_ICONS = [
  { value: 'star', label: '⭐ Star (Academics / Excellence)' },
  { value: 'users', label: '👥 Users (Mentorship / Community)' },
  { value: 'chart', label: '📊 Chart (Innovation / Growth)' },
  { value: 'heart', label: '❤️ Heart (Outreach / Impact)' },
  { value: 'book', label: '📘 Book (Research / Publications)' },
  { value: 'building', label: '🏛️ Building (Infrastructure)' },
];

let uid = 0;
const nextId = () => `row-${Date.now()}-${uid++}`;

const AdminPrincipal = () => {
  const { baseUrl, baseUrlForFiles } = useContext(AppContext);

  const [activeSection, setActiveSection] = useState('hero');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [formError, setFormError] = useState('');
  const [principalId, setPrincipalId] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // ---------- Hero ----------
  const [heroImage, setHeroImage] = useState(null);
  const [heroImagePreview, setHeroImagePreview] = useState(null);
  const [badgeText, setBadgeText] = useState('Meet Our Leader');
  const [titlePrefix, setTitlePrefix] = useState('Empowering');
  const [titleHighlight, setTitleHighlight] = useState("Tomorrow's");
  const [titleSuffix, setTitleSuffix] = useState('Leaders');
  const [heroDescription, setHeroDescription] = useState('');
  const [stats, setStats] = useState([
    { id: nextId(), number: '15+', label: 'Years of Excellence' },
    { id: nextId(), number: '5000+', label: 'Students Mentored' },
    { id: nextId(), number: '50+', label: 'Awards & Recognition' },
  ]);

  // ---------- Message ----------
  const [messageGreeting, setMessageGreeting] = useState('Dear Students, Parents, and Esteemed Faculty,');
  const [messageParagraphs, setMessageParagraphs] = useState([
    { id: nextId(), text: '' },
  ]);
  const [signatureName, setSignatureName] = useState('');
  const [signatureTitle, setSignatureTitle] = useState('Principal');

  // ---------- About ----------
  const [aboutImage, setAboutImage] = useState(null);
  const [aboutImagePreview, setAboutImagePreview] = useState(null);
  const [aboutParagraphs, setAboutParagraphs] = useState([
    { id: nextId(), text: '' },
  ]);
  const [credentialsTitle, setCredentialsTitle] = useState('Qualifications & Certifications');
  const [credentials, setCredentials] = useState([
    { id: nextId(), text: '' },
  ]);

  // ---------- Achievements ----------
  const [achievements, setAchievements] = useState([
    { id: nextId(), icon: 'star', title: '', description: '' },
  ]);

  const heroImageInputRef = useRef(null);
  const aboutImageInputRef = useRef(null);

  // ---------------------------------------------------------------------
  // Data loading
  // ---------------------------------------------------------------------
  const viewPrincipalData = () => {
    setLoading(true);
    axios
      .get(`${baseUrl}/admin/principal/viewPrincipal`)
      .then((response) => {
        const data = response.data?.principal;
        if (data) {
          setPrincipalId(data.id ?? null);
          setLastUpdated(data.updatedAt ?? null);

          setHeroImagePreview(data.heroImage ? `${baseUrlForFiles}/${data.heroImage}` : null);
          setBadgeText(data.badgeText ?? 'Meet Our Leader');
          setTitlePrefix(data.titlePrefix ?? 'Empowering');
          setTitleHighlight(data.titleHighlight ?? "Tomorrow's");
          setTitleSuffix(data.titleSuffix ?? 'Leaders');
          setHeroDescription(data.heroDescription ?? '');
          setStats(
            Array.isArray(data.stats) && data.stats.length
              ? data.stats.map((s) => ({ id: nextId(), number: s.number, label: s.label }))
              : stats
          );

          setMessageGreeting(data.messageGreeting ?? '');
          setMessageParagraphs(
            Array.isArray(data.messageParagraphs) && data.messageParagraphs.length
              ? data.messageParagraphs.map((t) => ({ id: nextId(), text: t }))
              : messageParagraphs
          );
          setSignatureName(data.signatureName ?? '');
          setSignatureTitle(data.signatureTitle ?? 'Principal');

          setAboutImagePreview(data.aboutImage ? `${baseUrlForFiles}/${data.aboutImage}` : null);
          setAboutParagraphs(
            Array.isArray(data.aboutParagraphs) && data.aboutParagraphs.length
              ? data.aboutParagraphs.map((t) => ({ id: nextId(), text: t }))
              : aboutParagraphs
          );
          setCredentialsTitle(data.credentialsTitle ?? 'Qualifications & Certifications');
          setCredentials(
            Array.isArray(data.credentials) && data.credentials.length
              ? data.credentials.map((t) => ({ id: nextId(), text: t }))
              : credentials
          );

          setAchievements(
            Array.isArray(data.achievements) && data.achievements.length
              ? data.achievements.map((a) => ({
                  id: nextId(),
                  icon: a.icon ?? 'star',
                  title: a.title ?? '',
                  description: a.description ?? '',
                }))
              : achievements
          );
        }
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    viewPrincipalData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------------------------------------------------------------
  // Image handlers
  // ---------------------------------------------------------------------
  const handleHeroImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setHeroImage(file);
      setHeroImagePreview(URL.createObjectURL(file));
    }
  };

  const handleAboutImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAboutImage(file);
      setAboutImagePreview(URL.createObjectURL(file));
    }
  };

  // ---------------------------------------------------------------------
  // Dynamic list helpers (stats / paragraphs / credentials / achievements)
  // ---------------------------------------------------------------------
  const addStat = () => setStats([...stats, { id: nextId(), number: '', label: '' }]);
  const removeStat = (id) => setStats(stats.filter((s) => s.id !== id));
  const updateStat = (id, field, value) =>
    setStats(stats.map((s) => (s.id === id ? { ...s, [field]: value } : s)));

  const addMessageParagraph = () =>
    setMessageParagraphs([...messageParagraphs, { id: nextId(), text: '' }]);
  const removeMessageParagraph = (id) =>
    setMessageParagraphs(messageParagraphs.filter((p) => p.id !== id));
  const updateMessageParagraph = (id, value) =>
    setMessageParagraphs(messageParagraphs.map((p) => (p.id === id ? { ...p, text: value } : p)));

  const addAboutParagraph = () =>
    setAboutParagraphs([...aboutParagraphs, { id: nextId(), text: '' }]);
  const removeAboutParagraph = (id) =>
    setAboutParagraphs(aboutParagraphs.filter((p) => p.id !== id));
  const updateAboutParagraph = (id, value) =>
    setAboutParagraphs(aboutParagraphs.map((p) => (p.id === id ? { ...p, text: value } : p)));

  const addCredential = () => setCredentials([...credentials, { id: nextId(), text: '' }]);
  const removeCredential = (id) => setCredentials(credentials.filter((c) => c.id !== id));
  const updateCredential = (id, value) =>
    setCredentials(credentials.map((c) => (c.id === id ? { ...c, text: value } : c)));

  const addAchievement = () =>
    setAchievements([...achievements, { id: nextId(), icon: 'star', title: '', description: '' }]);
  const removeAchievement = (id) => setAchievements(achievements.filter((a) => a.id !== id));
  const updateAchievement = (id, field, value) =>
    setAchievements(achievements.map((a) => (a.id === id ? { ...a, [field]: value } : a)));

  // ---------------------------------------------------------------------
  // Save
  // ---------------------------------------------------------------------
  const validate = () => {
    if (!titlePrefix.trim() || !titleSuffix.trim()) {
      return 'Please fill in the hero title.';
    }
    if (!signatureName.trim()) {
      return "Please fill in the principal's name in the Message section.";
    }
    return '';
  };

  const handleSave = () => {
    const error = validate();
    if (error) {
      setFormError(error);
      setActiveSection(!titlePrefix.trim() || !titleSuffix.trim() ? 'hero' : 'message');
      return;
    }
    setFormError('');
    setSaveMessage('');

    const formData = new FormData();
    if (principalId) formData.append('id', principalId);
    if (heroImage) formData.append('heroImage', heroImage);
    if (aboutImage) formData.append('aboutImage', aboutImage);

    formData.append('badgeText', badgeText);
    formData.append('titlePrefix', titlePrefix);
    formData.append('titleHighlight', titleHighlight);
    formData.append('titleSuffix', titleSuffix);
    formData.append('heroDescription', heroDescription);
    formData.append('stats', JSON.stringify(stats.map(({ number, label }) => ({ number, label }))));

    formData.append('messageGreeting', messageGreeting);
    formData.append(
      'messageParagraphs',
      JSON.stringify(messageParagraphs.map((p) => p.text))
    );
    formData.append('signatureName', signatureName);
    formData.append('signatureTitle', signatureTitle);

    formData.append(
      'aboutParagraphs',
      JSON.stringify(aboutParagraphs.map((p) => p.text))
    );
    formData.append('credentialsTitle', credentialsTitle);
    formData.append('credentials', JSON.stringify(credentials.map((c) => c.text)));

    formData.append(
      'achievements',
      JSON.stringify(achievements.map(({ icon, title, description }) => ({ icon, title, description })))
    );

    setSaving(true);
    axios
      .post(`${baseUrl}/admin/principal/savePrincipal`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((response) => {
        console.log(response.data);
        setSaveMessage('Changes saved successfully.');
        viewPrincipalData();
      })
      .catch((error) => {
        console.log(error);
        setFormError('Something went wrong while saving. Please try again.');
      })
      .finally(() => setSaving(false));
  };

  const sections = [
    { key: 'hero', label: 'Hero' },
    { key: 'message', label: "Principal's Message" },
    { key: 'about', label: 'About' },
    { key: 'achievements', label: 'Achievements' },
  ];

  if (loading) {
    return (
      
        <div className="ap ap--loading">
          <div className="ap__loader" />
          <p>Loading Principal page content…</p>
        </div>
      
    );
  }

  return (
    
      <div className="ap">
        <Container fluid className="ap__container">
          {/* ── Header ── */}
          <header className="ap__header">
            <div>
              <h1 className="ap__header-title">Principal Page</h1>
              <p className="ap__header-sub">Manage the content shown on the public Principal page</p>
            </div>
            <div className="ap__header-actions">
              {saveMessage && <span className="ap__save-toast">{saveMessage}</span>}
              <Button className="ap__btn-save" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save Changes'}
              </Button>
            </div>
          </header>

          {formError && <div className="ap__form-error">{formError}</div>}

          {/* ── Stats overview ── */}
          <Row className="ap__stats-grid g-3">
            <Col xs={12} sm={6} lg={3}>
              <div className="ap__stat ap__stat--accent">
                <div className="ap__stat-label">Hero Stats</div>
                <div className="ap__stat-value">{stats.length}</div>
                <div className="ap__stat-meta">Highlighted counters</div>
              </div>
            </Col>
            <Col xs={12} sm={6} lg={3}>
              <div className="ap__stat">
                <div className="ap__stat-label">Achievements</div>
                <div className="ap__stat-value">{achievements.length}</div>
                <div className="ap__stat-meta">Cards on page</div>
              </div>
            </Col>
            <Col xs={12} sm={6} lg={3}>
              <div className="ap__stat">
                <div className="ap__stat-label">Credentials</div>
                <div className="ap__stat-value">{credentials.length}</div>
                <div className="ap__stat-meta">Listed qualifications</div>
              </div>
            </Col>
            <Col xs={12} sm={6} lg={3}>
              <div className="ap__stat">
                <div className="ap__stat-label">Last Updated</div>
                <div className="ap__stat-value ap__stat-value--sm">
                  {lastUpdated ? new Date(lastUpdated).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                </div>
                <div className="ap__stat-meta">Published state</div>
              </div>
            </Col>
          </Row>

          {/* ── Section switcher ── */}
          <Nav variant="pills" className="ap__nav" activeKey={activeSection} onSelect={(k) => setActiveSection(k)}>
            {sections.map((s) => (
              <Nav.Item key={s.key}>
                <Nav.Link eventKey={s.key} className="ap__nav-link">
                  {s.label}
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>

          {/* ── Hero Section ── */}
          {activeSection === 'hero' && (
            <Card className="ap__card">
              <Card.Body>
                <h2 className="ap__section-title">Hero Section</h2>
                <Row className="g-4">
                  <Col md={4}>
                    <Form.Label className="ap__form-label">Principal Photo</Form.Label>
                    <input
                      type="file"
                      accept="image/*"
                      ref={heroImageInputRef}
                      style={{ display: 'none' }}
                      onChange={handleHeroImageChange}
                    />
                    <div className="ap__image-uploader" onClick={() => heroImageInputRef.current.click()}>
                      {heroImagePreview ? (
                        <img src={heroImagePreview} alt="Hero preview" className="ap__image-preview" />
                      ) : (
                        <div className="ap__image-placeholder">
                          <span className="ap__image-icon">🖼️</span>
                          <span className="ap__image-hint">Click to upload</span>
                        </div>
                      )}
                    </div>
                  </Col>

                  <Col md={8}>
                    <Row className="g-3">
                      <Col xs={12}>
                        <Form.Label className="ap__form-label">Eyebrow Badge Text</Form.Label>
                        <Form.Control
                          className="ap__form-input"
                          value={badgeText}
                          onChange={(e) => setBadgeText(e.target.value)}
                          placeholder="Meet Our Leader"
                        />
                      </Col>
                      <Col xs={12} md={4}>
                        <Form.Label className="ap__form-label">Title Prefix <span className="ap__required">*</span></Form.Label>
                        <Form.Control
                          className="ap__form-input"
                          value={titlePrefix}
                          onChange={(e) => setTitlePrefix(e.target.value)}
                        />
                      </Col>
                      <Col xs={12} md={4}>
                        <Form.Label className="ap__form-label">Title Highlight <span className="ap__required">*</span></Form.Label>
                        <Form.Control
                          className="ap__form-input"
                          value={titleHighlight}
                          onChange={(e) => setTitleHighlight(e.target.value)}
                        />
                      </Col>
                      <Col xs={12} md={4}>
                        <Form.Label className="ap__form-label">Title Suffix <span className="ap__required">*</span></Form.Label>
                        <Form.Control
                          className="ap__form-input"
                          value={titleSuffix}
                          onChange={(e) => setTitleSuffix(e.target.value)}
                        />
                      </Col>
                      <Col xs={12}>
                        <Form.Label className="ap__form-label">Hero Description</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          className="ap__form-input ap__form-textarea"
                          value={heroDescription}
                          onChange={(e) => setHeroDescription(e.target.value)}
                        />
                      </Col>
                    </Row>
                  </Col>
                </Row>

                <hr className="ap__divider" />

                <div className="ap__subheader">
                  <h3 className="ap__subsection-title">Hero Stats</h3>
                  <Button className="ap__btn-add" onClick={addStat}>+ Add Stat</Button>
                </div>

                {stats.map((stat, index) => (
                  <Row key={stat.id} className="ap__repeat-row g-3">
                    <Col xs={4} md={3}>
                      <Form.Control
                        className="ap__form-input"
                        placeholder="15+"
                        value={stat.number}
                        onChange={(e) => updateStat(stat.id, 'number', e.target.value)}
                      />
                    </Col>
                    <Col xs={6} md={8}>
                      <Form.Control
                        className="ap__form-input"
                        placeholder="Years of Excellence"
                        value={stat.label}
                        onChange={(e) => updateStat(stat.id, 'label', e.target.value)}
                      />
                    </Col>
                    <Col xs={2} md={1} className="ap__repeat-remove">
                      <button
                        className="ap__action-btn ap__action-btn--delete"
                        onClick={() => removeStat(stat.id)}
                        disabled={stats.length === 1}
                        title="Remove stat"
                        type="button"
                      >
                        ✕
                      </button>
                    </Col>
                  </Row>
                ))}
              </Card.Body>
            </Card>
          )}

          {/* ── Message Section ── */}
          {activeSection === 'message' && (
            <Card className="ap__card">
              <Card.Body>
                <h2 className="ap__section-title">Principal's Message</h2>
                <Row className="g-3">
                  <Col xs={12}>
                    <Form.Label className="ap__form-label">Opening Greeting</Form.Label>
                    <Form.Control
                      className="ap__form-input"
                      value={messageGreeting}
                      onChange={(e) => setMessageGreeting(e.target.value)}
                      placeholder="Dear Students, Parents, and Esteemed Faculty,"
                    />
                  </Col>
                </Row>

                <hr className="ap__divider" />

                <div className="ap__subheader">
                  <h3 className="ap__subsection-title">Message Paragraphs</h3>
                  <Button className="ap__btn-add" onClick={addMessageParagraph}>+ Add Paragraph</Button>
                </div>

                {messageParagraphs.map((para, index) => (
                  <Row key={para.id} className="ap__repeat-row g-3">
                    <Col xs={10} md={11}>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        className="ap__form-input ap__form-textarea"
                        placeholder={`Paragraph ${index + 1}`}
                        value={para.text}
                        onChange={(e) => updateMessageParagraph(para.id, e.target.value)}
                      />
                    </Col>
                    <Col xs={2} md={1} className="ap__repeat-remove">
                      <button
                        className="ap__action-btn ap__action-btn--delete"
                        onClick={() => removeMessageParagraph(para.id)}
                        disabled={messageParagraphs.length === 1}
                        title="Remove paragraph"
                        type="button"
                      >
                        ✕
                      </button>
                    </Col>
                  </Row>
                ))}

                <hr className="ap__divider" />

                <Row className="g-3">
                  <Col xs={12} md={6}>
                    <Form.Label className="ap__form-label">Signature Name <span className="ap__required">*</span></Form.Label>
                    <Form.Control
                      className="ap__form-input"
                      value={signatureName}
                      onChange={(e) => setSignatureName(e.target.value)}
                      placeholder="Dr. Sarah Johnson"
                    />
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Label className="ap__form-label">Signature Title</Form.Label>
                    <Form.Control
                      className="ap__form-input"
                      value={signatureTitle}
                      onChange={(e) => setSignatureTitle(e.target.value)}
                      placeholder="Principal"
                    />
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          )}

          {/* ── About Section ── */}
          {activeSection === 'about' && (
            <Card className="ap__card">
              <Card.Body>
                <h2 className="ap__section-title">About the Principal</h2>
                <Row className="g-4">
                  <Col md={4}>
                    <Form.Label className="ap__form-label">About Photo</Form.Label>
                    <input
                      type="file"
                      accept="image/*"
                      ref={aboutImageInputRef}
                      style={{ display: 'none' }}
                      onChange={handleAboutImageChange}
                    />
                    <div className="ap__image-uploader" onClick={() => aboutImageInputRef.current.click()}>
                      {aboutImagePreview ? (
                        <img src={aboutImagePreview} alt="About preview" className="ap__image-preview" />
                      ) : (
                        <div className="ap__image-placeholder">
                          <span className="ap__image-icon">🖼️</span>
                          <span className="ap__image-hint">Click to upload</span>
                        </div>
                      )}
                    </div>
                  </Col>

                  <Col md={8}>
                    <div className="ap__subheader">
                      <h3 className="ap__subsection-title">About Paragraphs</h3>
                      <Button className="ap__btn-add" onClick={addAboutParagraph}>+ Add Paragraph</Button>
                    </div>
                    {aboutParagraphs.map((para, index) => (
                      <Row key={para.id} className="ap__repeat-row g-3">
                        <Col xs={10}>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            className="ap__form-input ap__form-textarea"
                            placeholder={`Paragraph ${index + 1}`}
                            value={para.text}
                            onChange={(e) => updateAboutParagraph(para.id, e.target.value)}
                          />
                        </Col>
                        <Col xs={2} className="ap__repeat-remove">
                          <button
                            className="ap__action-btn ap__action-btn--delete"
                            onClick={() => removeAboutParagraph(para.id)}
                            disabled={aboutParagraphs.length === 1}
                            title="Remove paragraph"
                            type="button"
                          >
                            ✕
                          </button>
                        </Col>
                      </Row>
                    ))}
                  </Col>
                </Row>

                <hr className="ap__divider" />

                <Row className="g-3">
                  <Col xs={12}>
                    <Form.Label className="ap__form-label">Credentials Section Title</Form.Label>
                    <Form.Control
                      className="ap__form-input"
                      value={credentialsTitle}
                      onChange={(e) => setCredentialsTitle(e.target.value)}
                    />
                  </Col>
                </Row>

                <div className="ap__subheader">
                  <h3 className="ap__subsection-title">Credentials</h3>
                  <Button className="ap__btn-add" onClick={addCredential}>+ Add Credential</Button>
                </div>

                {credentials.map((cred) => (
                  <Row key={cred.id} className="ap__repeat-row g-3">
                    <Col xs={10} md={11}>
                      <Form.Control
                        className="ap__form-input"
                        placeholder="Ph.D. in Educational Leadership - Stanford University"
                        value={cred.text}
                        onChange={(e) => updateCredential(cred.id, e.target.value)}
                      />
                    </Col>
                    <Col xs={2} md={1} className="ap__repeat-remove">
                      <button
                        className="ap__action-btn ap__action-btn--delete"
                        onClick={() => removeCredential(cred.id)}
                        disabled={credentials.length === 1}
                        title="Remove credential"
                        type="button"
                      >
                        ✕
                      </button>
                    </Col>
                  </Row>
                ))}
              </Card.Body>
            </Card>
          )}

          {/* ── Achievements Section ── */}
          {activeSection === 'achievements' && (
            <Card className="ap__card">
              <Card.Body>
                <div className="ap__subheader">
                  <h2 className="ap__section-title ap__section-title--flush">Achievements &amp; Impact</h2>
                  <Button className="ap__btn-add" onClick={addAchievement}>+ Add Achievement</Button>
                </div>

                <Row className="g-3">
                  {achievements.map((ach, index) => (
                    <Col xs={12} md={6} key={ach.id}>
                      <div className="ap__achievement-card">
                        <div className="ap__achievement-card-head">
                          <Badge className="ap__badge-index">{index + 1}</Badge>
                          <button
                            className="ap__action-btn ap__action-btn--delete"
                            onClick={() => removeAchievement(ach.id)}
                            disabled={achievements.length === 1}
                            title="Remove achievement"
                            type="button"
                          >
                            ✕
                          </button>
                        </div>
                        <Form.Label className="ap__form-label">Icon</Form.Label>
                        <Form.Select
                          className="ap__form-input"
                          value={ach.icon}
                          onChange={(e) => updateAchievement(ach.id, 'icon', e.target.value)}
                        >
                          {ACHIEVEMENT_ICONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </Form.Select>

                        <Form.Label className="ap__form-label ap__form-label--mt">Title</Form.Label>
                        <Form.Control
                          className="ap__form-input"
                          placeholder="Academic Excellence"
                          value={ach.title}
                          onChange={(e) => updateAchievement(ach.id, 'title', e.target.value)}
                        />

                        <Form.Label className="ap__form-label ap__form-label--mt">Description</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          className="ap__form-input ap__form-textarea"
                          placeholder="Consistently achieved 98% pass rate…"
                          value={ach.description}
                          onChange={(e) => updateAchievement(ach.id, 'description', e.target.value)}
                        />
                      </div>
                    </Col>
                  ))}
                </Row>
              </Card.Body>
            </Card>
          )}

          {/* ── Bottom save bar (mirrors header action for long forms) ── */}
          <div className="ap__bottom-bar">
            <Button className="ap__btn-save" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save Changes'}
            </Button>
          </div>
        </Container>
      </div>
    
  );
};

export default AdminPrincipal;